import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MAX_COMPARE,
  MIN_COMPARE,
  parseCompareParam,
  nextSelection,
  toCompareParam,
} from '../utils/compareSelection';

export { MAX_COMPARE, MIN_COMPARE };

const PARAM = 'compare';
const VIEW = 'view';

export function useCompareSelection() {
  const [searchParams, setSearchParams] = useSearchParams();

  const compareIds = useMemo(() => parseCompareParam(searchParams.get(PARAM)), [searchParams]);

  const compareMode = searchParams.get(VIEW) === 'compare' && compareIds.length >= MIN_COMPARE;

  const write = useCallback(
    (ids: string[], mode: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const param = toCompareParam(ids);
          if (param) next.set(PARAM, param);
          else next.delete(PARAM);
          if (mode && ids.length >= MIN_COMPARE) next.set(VIEW, 'compare');
          else next.delete(VIEW);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggle = useCallback(
    (id: string) => {
      const next = nextSelection(compareIds, id);
      write(next, compareMode && next.length >= MIN_COMPARE);
    },
    [compareIds, compareMode, write],
  );

  const remove = useCallback(
    (id: string) => {
      const next = compareIds.filter((x) => x !== id);
      write(next, next.length >= MIN_COMPARE);
    },
    [compareIds, write],
  );

  const clear = useCallback(() => write([], false), [write]);
  const enterCompare = useCallback(() => write(compareIds, true), [compareIds, write]);
  const exitCompare = useCallback(() => write(compareIds, false), [compareIds, write]);

  return {
    compareIds,
    compareMode,
    canCompare: compareIds.length >= MIN_COMPARE,
    isFull: compareIds.length >= MAX_COMPARE,
    toggle,
    remove,
    clear,
    enterCompare,
    exitCompare,
  };
}

export default useCompareSelection;
